import { FC, useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FieldRenderProps } from 'react-final-form';

import classNames from 'classnames';
import './FileUploadField.styles.scss';

type FileUploadFieldProps = FieldRenderProps<File | null, HTMLElement> & {
  label?: string;
  accept?: Record<string, string[]>; // e.g. { 'text/csv': ['.csv'] }
  placeholder?: string;
  required?: boolean;
};

const FileUploadField: FC<FileUploadFieldProps> = ({
  input,
  meta,
  label = 'Upload file',
  accept,
  placeholder = 'Drag & drop or click to upload',
  required,
}) => {
  const [fileName, setFileName] = useState<string | null>(
    input.value ? (input.value as any)?.name : null,
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      input.onChange(file);
      setFileName(file.name);
    },
    [input],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    multiple: false,
  });

  const error = meta?.touched && meta?.error;
  const hasFile = Boolean(fileName);

  return (
    <div className="file-upload-field">
      {label && (
        <label className="file-upload-field__label">
          {label} {required && <span className="required">*</span>}
        </label>
      )}

      <div
        {...getRootProps()}
        className={classNames('file-upload-field__dropzone', {
          'file-upload-field__dropzone--active': isDragActive,
          'file-upload-field__dropzone--error': error,
        })}
      >
        {/* Only pass safe props to the file input (omit value) */}
        <input
          {...getInputProps({
            name: input.name,
            onBlur: input.onBlur,
            onFocus: input.onFocus,
          })}
        />

        {hasFile ? (
          <div className="file-upload-field__file">
            <span className="file-upload-field__filename">{fileName}</span>
            <button
              type="button"
              className="file-upload-field__remove"
              onClick={(e) => {
                e.stopPropagation();
                input.onChange(null);
                setFileName(null);
              }}
            >
              ×
            </button>
          </div>
        ) : (
          <div className="file-upload-field__placeholder">
            {isDragActive ? 'Drop file here…' : placeholder}
          </div>
        )}
      </div>

      {error && <span className="file-upload-field__error">{error}</span>}
    </div>
  );
};

export default FileUploadField;
